// Drawing rules of the top view's interactive editor (DeckDrawingPlane): where the next point goes,
// shared by the point actually placed on click/Enter and by the live preview drawn while hovering.
import type { PlaneCoordinate } from "./CartesianPlane";
import { coordinatesMatch, getSegmentLength, isMostlyHorizontal } from "./deckGeometry";

export const getClosingCoordinate = (
  currentCoordinates: PlaneCoordinate[],
  pointLimit: number,
): PlaneCoordinate | null => {
  if (currentCoordinates.length !== pointLimit - 1) {
    return null;
  }

  const firstCoordinate = currentCoordinates[0];
  const previousCoordinate = currentCoordinates[currentCoordinates.length - 1];
  const penultimateCoordinate = currentCoordinates[currentCoordinates.length - 2];

  if (!firstCoordinate || !previousCoordinate || !penultimateCoordinate) {
    return null;
  }

  const isPreviousSegmentHorizontal = previousCoordinate.y === penultimateCoordinate.y;
  const closingCoordinate = isPreviousSegmentHorizontal
    ? { x: previousCoordinate.x, y: firstCoordinate.y }
    : { x: firstCoordinate.x, y: previousCoordinate.y };

  return coordinatesMatch(closingCoordinate, previousCoordinate) || coordinatesMatch(closingCoordinate, firstCoordinate)
    ? null
    : closingCoordinate;
};

export const getNextCoordinate = (
  currentCoordinates: PlaneCoordinate[],
  nextPoint: PlaneCoordinate,
  pointLimit: number,
): PlaneCoordinate | null => {
  const firstCoordinate = currentCoordinates[0];
  const previousCoordinate = currentCoordinates[currentCoordinates.length - 1];
  const penultimateCoordinate = currentCoordinates[currentCoordinates.length - 2];

  if (!firstCoordinate || !previousCoordinate) {
    return { x: nextPoint.x, y: nextPoint.y };
  }

  if (currentCoordinates.length === 1) {
    const nextCoordinate = isMostlyHorizontal(firstCoordinate, nextPoint)
      ? { x: nextPoint.x, y: firstCoordinate.y }
      : { x: firstCoordinate.x, y: nextPoint.y };

    return coordinatesMatch(nextCoordinate, firstCoordinate) ? null : nextCoordinate;
  }

  if (!penultimateCoordinate) {
    return null;
  }

  const isPreviousSegmentHorizontal = previousCoordinate.y === penultimateCoordinate.y;

  if (currentCoordinates.length === pointLimit - 1) {
    return getClosingCoordinate(currentCoordinates, pointLimit);
  }

  const nextCoordinate = isPreviousSegmentHorizontal
    ? { x: previousCoordinate.x, y: nextPoint.y }
    : { x: nextPoint.x, y: previousCoordinate.y };

  return coordinatesMatch(nextCoordinate, previousCoordinate) ? null : nextCoordinate;
};

// Same axis/direction logic as getNextCoordinate (mouse-driven for the 2nd point, alternating
// afterwards), but the segment length is the typed distance instead of the raw hover position -
// lets the user point roughly where the next side should go and type its exact length. The
// closing point (length === pointLimit - 1) has no positional freedom, so typing is a no-op there.
export const getTypedNextCoordinate = (
  currentCoordinates: PlaneCoordinate[],
  pointLimit: number,
  hoverPoint: PlaneCoordinate,
  distance: number,
): PlaneCoordinate | null => {
  const firstCoordinate = currentCoordinates[0];
  const previousCoordinate = currentCoordinates[currentCoordinates.length - 1];
  const penultimateCoordinate = currentCoordinates[currentCoordinates.length - 2];

  if (!firstCoordinate || !previousCoordinate || currentCoordinates.length >= pointLimit - 1) {
    return null;
  }

  let isHorizontal: boolean;

  if (currentCoordinates.length === 1) {
    isHorizontal = isMostlyHorizontal(firstCoordinate, hoverPoint);
  } else {
    if (!penultimateCoordinate) {
      return null;
    }

    isHorizontal = previousCoordinate.y !== penultimateCoordinate.y;
  }

  const sign = isHorizontal
    ? hoverPoint.x >= previousCoordinate.x
      ? 1
      : -1
    : hoverPoint.y >= previousCoordinate.y
      ? 1
      : -1;

  return isHorizontal
    ? { x: previousCoordinate.x + sign * distance, y: previousCoordinate.y }
    : { x: previousCoordinate.x, y: previousCoordinate.y + sign * distance };
};

// The typed distance (see getTypedNextCoordinate) when it's a usable length, null otherwise - e.g.
// nothing typed yet, or only a "." so far.
export const parseTypedDistance = (distanceText: string): number | null => {
  const distance = distanceText ? Number(distanceText) : null;

  return distance != null && Number.isFinite(distance) && distance > 0 ? distance : null;
};

// Vertical boards stack side by side along the base (horizontal) axis, horizontal boards stack
// along the height (vertical) axis - so a segment only needs to clear one board's width when its
// own orientation is the one boards are laid across, matching the same rule getDimensionBounds
// applies to base/height/secondaryBase/secondaryHeight once the shape is complete.
export const isSegmentNarrowerThanBoardWidth = (
  previousCoordinate: PlaneCoordinate,
  candidateCoordinate: PlaneCoordinate,
  boardWidthMm: number | null | undefined,
  hasHorizontalBoards: boolean | null | undefined,
): boolean => {
  if (!boardWidthMm || boardWidthMm <= 0 || hasHorizontalBoards == null) {
    return false;
  }

  const isHorizontalSegment = candidateCoordinate.y === previousCoordinate.y;
  const constrainedByBoardWidth = isHorizontalSegment ? !hasHorizontalBoards : hasHorizontalBoards;

  if (!constrainedByBoardWidth) {
    return false;
  }

  return getSegmentLength(previousCoordinate, candidateCoordinate) < boardWidthMm / 1000;
};
